/**
 * Privacy Policy Modal component
 */

import React from 'react';
import { X } from 'lucide-react';

/**
 * Privacy Policy Modal component
 * 
 * @param {Object} props - Component props
 * @param {boolean} props.isOpen - Whether the modal is open
 * @param {Function} props.onClose - Function to close the modal
 * @param {Function} props.onAccept - Function to accept the privacy policy
 * @returns {JSX.Element|null} Privacy Policy Modal component or null if not open
 */
const PrivacyPolicyModal = ({ isOpen, onClose, onAccept }) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
        {/* Background overlay */}
        <div className="fixed inset-0 transition-opacity" aria-hidden="true">
          <div className="absolute inset-0 bg-gray-500 opacity-75"></div>
        </div>

        {/* Modal */}
        <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
          {/* Header */}
          <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
            <div className="flex items-start justify-between">
              <h3 className="text-lg font-medium text-gray-900">
                Privacy Policy & Terms of Service
              </h3>
              <button
                onClick={onClose}
                className="bg-white rounded-md text-gray-400 hover:text-gray-500 focus:outline-none"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            
            {/* Content */}
            <div className="mt-4 h-60 overflow-y-auto text-sm text-gray-600 pr-2">
              <h4 className="font-medium text-gray-900 mb-2">Privacy Policy</h4>
              <p className="mb-4">
                Welcome to VarsityLink. We are committed to protecting your privacy. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you use our service.
              </p>
              
              <h5 className="font-medium mb-1">Information We Collect</h5>
              <p className="mb-3">
                We collect information you provide directly to us when you register for an account, create or modify your profile, and interact with other users. This information may include your name, email address, university affiliation, photos, and personal preferences.
              </p>
              
              <h5 className="font-medium mb-1">How We Use Your Information</h5>
              <p className="mb-3">
                We use your information to provide, maintain, and improve our services, including personalizing your experience, connecting you with other students, and ensuring safety within our platform.
              </p>
              
              <h5 className="font-medium mb-1">Sharing of Information</h5>
              <p className="mb-3">
                We may share your information with other users as part of the core functionality of our service. Your profile information may be visible to other users according to your privacy settings.
              </p>
              

              <h4 className="font-medium text-gray-900 mb-2 mt-4">Terms of Service</h4>
              <p className="mb-4">
                By using VarsityLink, you agree to these terms which govern your use of our service.
              </p>
              
              <h5 className="font-medium mb-1">User Accounts</h5>
              <p className="mb-3">
                You must create an account with a valid university email address to use VarsityLink. You are responsible for maintaining the confidentiality of your account credentials and for all activities that occur under your account.
              </p>
              
              <h5 className="font-medium mb-1">User Conduct</h5>
              <p className="mb-3">
                You agree not to use the service to post illegal, harmful, threatening, abusive, or objectionable content. You will not impersonate others or attempt to gain unauthorized access to the service.
              </p>
              
              <h5 className="font-medium mb-1">Termination</h5>
              <p className="mb-3">
                We reserve the right to suspend or terminate your account if you violate these terms or for any other reason at our discretion.
              </p>
              
              <h5 className="font-medium mb-1">Modifications to the Service</h5>
              <p>
                We may modify or discontinue the service at any time, with or without notice. We shall not be liable to you or any third party for any modification, suspension, or discontinuance of the service.
              </p>
            </div>
          </div>
          
          {/* Actions */}
          <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
            <button
              type="button"
              onClick={onAccept}
              className="w-full inline-flex justify-center rounded-xl border border-transparent shadow-sm px-4 py-2 bg-green-500 text-base font-medium text-white hover:bg-green-600 focus:outline-none sm:ml-3 sm:w-auto sm:text-sm"
            >
              I Accept
            </button>
            <button
              type="button"
              onClick={onClose}
              className="mt-3 w-full inline-flex justify-center rounded-xl border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
            >
              Decline
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PrivacyPolicyModal;